import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { toast } from "sonner"
import {
  runUploadPipeline,
  FileProgress,
  PipelineResult,
} from "../utils/uploadPipeline"

const TERMINAL_STATUSES: FileProgress["status"][] = [
  "success",
  "failed",
  "skipped",
  "unsupported",
]

export const uploadFiles = createAsyncThunk<
  PipelineResult,
  { files: File[]; eventId: string; folderId: string; hostId: string },
  { rejectValue: string }
>("upload/uploadFiles", async (data, { rejectWithValue, dispatch }) => {
  try {
    // Seed one row per selected file so the UI can show per-file progress.
    dispatch(
      initFiles(
        data.files.map((f, i) => ({
          clientRef: `file-${i}`,
          name: f.name,
          status: "pending" as const,
          progress: 0,
        }))
      )
    )

    return await runUploadPipeline(
      data.files,
      {
        eventId: data.eventId,
        userId: data.hostId,
        folderId: data.folderId || null,
      },
      (clientRef, patch) => dispatch(updateFile({ clientRef, patch }))
    )
  } catch (error: any) {
    const message =
      error?.response?.data?.error || error?.message || "Upload failed"
    toast.error(message)
    return rejectWithValue(message)
  }
})

interface UploadState {
  uploading: boolean
  uploadProgress: number
  files: FileProgress[]
  failedFileIds: string[]
  skipped: Array<{ name: string; reason: string }>
  error: string | null
  success: boolean
}

const initialState: UploadState = {
  uploading: false,
  uploadProgress: 0,
  files: [],
  failedFileIds: [],
  skipped: [],
  error: null,
  success: false,
}

const uploadSlice = createSlice({
  name: "upload",
  initialState,
  reducers: {
    resetUploadState: (state) => {
      state.uploading = false
      state.uploadProgress = 0
      state.files = []
      state.failedFileIds = []
      state.skipped = []
      state.error = null
      state.success = false
    },
    initFiles: (state, action: PayloadAction<FileProgress[]>) => {
      state.files = action.payload
      state.uploadProgress = 0
    },
    updateFile: (
      state,
      action: PayloadAction<{
        clientRef: string
        patch: Partial<FileProgress>
      }>
    ) => {
      const file = state.files.find(
        (f) => f.clientRef === action.payload.clientRef
      )
      if (!file) return
      Object.assign(file, action.payload.patch)
      // Terminal states finish their bar so the aggregate never stalls.
      if (TERMINAL_STATUSES.includes(file.status)) file.progress = 100
      state.uploadProgress = state.files.length
        ? Math.round(
            state.files.reduce((s, f) => s + (f.progress || 0), 0) /
              state.files.length
          )
        : 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadFiles.pending, (state) => {
      state.uploading = true
      state.error = null
      state.success = false
      state.uploadProgress = 0
      state.files = []
      state.failedFileIds = []
      state.skipped = []
    })
    builder.addCase(uploadFiles.fulfilled, (state, action) => {
      state.uploading = false
      state.success = true
      state.uploadProgress = 100
      state.failedFileIds = action.payload.failedFileIds
      state.skipped = action.payload.skipped

      const confirmed = action.payload.confirmedFileIds.length
      const failed = action.payload.failedFileIds.length
      const skipped = action.payload.skipped.length

      if (failed === 0 && skipped === 0) {
        toast.success(
          confirmed > 0
            ? `${confirmed} file${confirmed > 1 ? "s" : ""} uploaded successfully!`
            : "Files uploaded successfully!"
        )
      } else {
        const parts: string[] = []
        if (confirmed > 0) parts.push(`${confirmed} uploaded`)
        if (failed > 0) parts.push(`${failed} failed`)
        if (skipped > 0) parts.push(`${skipped} skipped`)
        toast.warning(parts.join(", "))
      }
    })
    builder.addCase(uploadFiles.rejected, (state, action) => {
      state.uploading = false
      state.error = (action.payload as string) || "Upload failed"
      state.uploadProgress = 0
    })
  },
})

export const { resetUploadState, initFiles, updateFile } = uploadSlice.actions
export default uploadSlice.reducer
